/**
 * The task page's dialog controller: which dialog is open, the idempotency
 * key minted per opening, the in-flight and failure state, the candidate
 * list for the prerequisite picker, and the one mutation path that turns a
 * confirmed dialog into the request the server expects.
 */

import { useCallback, useMemo, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import {
  addTaskReference,
  ApiFailure,
  deleteTask,
  editTask,
  fetchDataRoom,
  fetchTasks,
  newIdempotencyKey,
  replaceTaskDependencies,
  transitionTask,
  type TaskView,
} from '../../api.js';
import type { WorkspaceCopy } from '../../copy.js';
import { InlineAlert } from '../../ui.js';
import { mergeExpectationApplies } from './merge-expectation.js';
import type { TaskDialogFields } from './useTaskDialogFields.js';

export type DialogKind =
  | 'claim'
  | 'submit'
  | 'approve'
  | 'reject'
  | 'complete'
  | 'complete_own'
  | 'return'
  | 'reference'
  | 'dependencies'
  | 'edit'
  | 'delete'
  | null;

export function useTaskDialogs({
  copy,
  announce,
  navigate,
  reload,
  mayUseDataRoom,
  fields,
}: {
  readonly copy: WorkspaceCopy;
  readonly announce: (message: string) => void;
  readonly navigate: NavigateFunction;
  readonly reload: () => void;
  readonly mayUseDataRoom: boolean;
  readonly fields: TaskDialogFields;
}) {
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [dialogKey, setDialogKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionFailure, setActionFailure] = useState<ApiFailure | null>(null);
  const {
    reason,
    setReason,
    priority,
    setPriority,
    requiresMerge,
    setRequiresMerge,
    editType,
    setEditType,
    editDifficulty,
    setEditDifficulty,
    editDescription,
    setEditDescription,
    issueUrl,
    setIssueUrl,
    pullRequestUrl,
    setPullRequestUrl,
    mergeUrl,
    setMergeUrl,
    referenceKind,
    setReferenceKind,
    referenceUrl,
    setReferenceUrl,
    dependencyIds,
    setDependencyIds,
    setDependencyCandidates,
    submitDocumentId,
    setSubmitDocumentId,
    note,
    setNote,
    editTitle,
    setEditTitle,
    editSuggestedPriority,
    setEditSuggestedPriority,
    editPriority,
    setEditPriority,
    editUnassign,
    setEditUnassign,
    setDocumentChoices,
  } = fields;

  // The candidate list is fetched on every dialog opening — current server
  // data, never a snapshot — minus this task itself, so "depend on myself"
  // is not even offerable. A failure is a failure state with a retry.
  const loadDependencyCandidates = useCallback((selfTaskId: string) => {
    setDependencyCandidates({ status: 'loading' });
    fetchTasks()
      .then((listing) => {
        setDependencyCandidates({
          status: 'ready',
          tasks: listing.tasks.filter((candidate) => candidate.id !== selfTaskId),
        });
      })
      .catch(() => {
        setDependencyCandidates({ status: 'failed' });
      });
  }, []);

  const openDialog = useCallback(
    (kind: Exclude<DialogKind, null>, view: TaskView) => {
      setDialog(kind);
      setDialogKey(newIdempotencyKey());
      setActionFailure(null);
      setReason('');
      setPriority(view.task.suggestedPriority);
      setRequiresMerge(view.task.requiresMerge);
      // The approval dialog opens on the proposal as written: confirming
      // without touching anything approves exactly what was proposed, and any
      // change the Owner makes is visible in the fields before it is sent.
      setEditType(view.task.taskType);
      setEditDifficulty(view.task.difficulty);
      setEditDescription(view.task.description);
      setIssueUrl('');
      setPullRequestUrl('');
      setMergeUrl('');
      setReferenceKind('MERGE');
      setReferenceUrl('');
      setDependencyIds(view.dependencies.map((edge) => edge.taskId));
      setSubmitDocumentId('');
      setNote('');
      // The edit dialog opens on the task as it stands, so confirming
      // without touching anything changes nothing at all.
      setEditTitle(view.task.title);
      setEditSuggestedPriority(view.task.suggestedPriority);
      setEditPriority(view.task.priority);
      setEditUnassign(false);
      // An edit may retype and re-prerequisite from any state, so its
      // candidate list is loaded on the same terms the dependency dialog uses.
      if (kind === 'dependencies' || kind === 'edit') {
        loadDependencyCandidates(view.task.id);
      }
      if (kind === 'submit' && mayUseDataRoom) {
        // The document picker: research and decision tasks submit a Data Room
        // document. A failed load leaves the picker empty — links still work.
        fetchDataRoom()
          .then((listing) => {
            setDocumentChoices(listing.documents);
          })
          .catch(() => {
            setDocumentChoices([]);
          });
      }
    },
    [mayUseDataRoom, loadDependencyCandidates],
  );

  const closeDialog = useCallback(() => {
    if (!busy) {
      setDialog(null);
      setActionFailure(null);
    }
  }, [busy]);

  const act = useCallback(
    (view: TaskView, kind: Exclude<DialogKind, null>) => {
      const task = view.task;
      // Approval without a priority is refused here, before any request:
      // the server would 400 it anyway (a default would be an unattributed
      // decision), but the person deserves the sentence, not the status code.
      if (kind === 'approve' && priority === null) {
        setActionFailure(
          new ApiFailure('invalid', { detail: copy.dialogs.priorityMissing }),
        );
        return;
      }
      setBusy(true);
      setActionFailure(null);

      const run = async (): Promise<void> => {
        if (kind === 'delete') {
          await deleteTask(dialogKey, task.id, { expectedVersion: task.version });
          announce(copy.deletion.deleted(task.reference));
          // The task no longer exists as far as the API is concerned;
          // reloading this page would only render "not available". The board
          // refetches on mount, so every affected list and count is fresh.
          await navigate('/tasks', { replace: true });
          return;
        }
        if (kind === 'dependencies') {
          await replaceTaskDependencies(dialogKey, task.id, {
            expectedVersion: task.version,
            prerequisiteTaskIds: dependencyIds,
          });
          announce(copy.execution.editDependencies);
          return;
        }
        if (kind === 'reference') {
          await addTaskReference(dialogKey, task.id, {
            kind: referenceKind ?? 'MERGE',
            url: referenceUrl.trim(),
          });
          announce(copy.toasts.referenceAdded);
          return;
        }
        if (kind === 'edit') {
          // Only what actually differs travels, so the recorded before/after
          // summary is a record of changes rather than of echoes.
          const edits: {
            expectedVersion: number;
            title?: string;
            description?: string;
            taskType?: string;
            difficulty?: string;
            suggestedPriority?: string;
            priority?: string;
            requiresMerge?: boolean;
            prerequisiteTaskIds?: readonly string[];
            unassign?: boolean;
          } = { expectedVersion: task.version };
          if (editTitle.trim() !== task.title) edits.title = editTitle.trim();
          if (editDescription.trim() !== task.description.trim()) {
            edits.description = editDescription;
          }
          if (editType !== null && editType !== task.taskType) {
            edits.taskType = editType;
          }
          if (editDifficulty !== null && editDifficulty !== task.difficulty) {
            edits.difficulty = editDifficulty;
          }
          if (
            editSuggestedPriority !== null &&
            editSuggestedPriority !== task.suggestedPriority
          ) {
            edits.suggestedPriority = editSuggestedPriority;
          }
          if (
            task.status !== 'BACKLOG' &&
            editPriority !== null &&
            editPriority !== task.priority
          ) {
            edits.priority = editPriority;
          }
          if (requiresMerge !== task.requiresMerge) {
            edits.requiresMerge = requiresMerge;
          }
          const currentDependencies = view.dependencies.map((edge) => edge.taskId);
          const sameDependencies =
            currentDependencies.length === dependencyIds.length &&
            currentDependencies.every((id) => dependencyIds.includes(id));
          if (!sameDependencies) edits.prerequisiteTaskIds = dependencyIds;
          if (editUnassign) edits.unassign = true;

          const result = await editTask(dialogKey, task.id, edits);
          announce(result.changed ? copy.edit.saved : copy.edit.noChanges);
          return;
        }

        const body: Record<string, unknown> = { expectedVersion: task.version };
        if (kind === 'approve') {
          body['priority'] = priority;
          const approvedType = editType ?? task.taskType;
          // Confirm-or-edit: every field travels only when it differs from
          // the proposal, so the history's "what the approver changed"
          // record stays a record of changes rather than of echoes.
          if (approvedType !== task.taskType) body['taskType'] = approvedType;
          if (editDifficulty !== null && editDifficulty !== task.difficulty) {
            body['difficulty'] = editDifficulty;
          }
          if (editDescription.trim() !== task.description.trim()) {
            body['description'] = editDescription;
          }
          body['requiresMerge'] = mergeExpectationApplies(approvedType)
            ? requiresMerge
            : false;
        }
        if (kind === 'reject' || kind === 'return') body['reason'] = reason;
        if (kind === 'submit' || kind === 'complete_own') {
          // Empty is a legitimate answer; the server stores it as no note.
          if (note.trim().length > 0) body['note'] = note;
        }
        if (kind === 'submit') {
          if (submitDocumentId !== null && submitDocumentId !== '') {
            body['documentId'] = submitDocumentId;
          }
          // Every link is optional, for every task type. Only what was
          // actually typed travels — nothing is sent to be refused.
          if (issueUrl.trim().length > 0) body['issueUrl'] = issueUrl.trim();
          if (pullRequestUrl.trim().length > 0) {
            body['pullRequestUrl'] = pullRequestUrl.trim();
          }
          if (mergeUrl.trim().length > 0) body['mergeUrl'] = mergeUrl.trim();
        }

        await transitionTask(dialogKey, task.id, kind, body);
        const toastFor: Record<string, (reference: string) => string> = {
          claim: copy.toasts.claimed,
          submit: copy.toasts.submitted,
          approve: copy.toasts.approved,
          reject: copy.toasts.rejected,
          complete: copy.toasts.completed,
          complete_own: copy.toasts.completed,
          return: copy.toasts.returned,
        };
        announce(
          (toastFor[kind] ?? ((reference: string) => reference))(task.reference),
        );
      };

      run()
        .then(() => {
          setBusy(false);
          setDialog(null);
          reload();
        })
        .catch((failure: unknown) => {
          setBusy(false);
          setActionFailure(
            failure instanceof ApiFailure ? failure : new ApiFailure('failed'),
          );
        });
    },
    [
      dialogKey,
      reason,
      priority,
      requiresMerge,
      editType,
      editDifficulty,
      editDescription,
      issueUrl,
      pullRequestUrl,
      mergeUrl,
      referenceKind,
      referenceUrl,
      dependencyIds,
      submitDocumentId,
      note,
      editTitle,
      editSuggestedPriority,
      editPriority,
      editUnassign,
      announce,
      copy,
      reload,
      navigate,
    ],
  );

  const failureAlert = useMemo(() => {
    if (actionFailure === null) return null;
    const [title, body] =
      actionFailure.kind === 'conflict'
        ? [copy.states.conflictTitle, copy.states.conflictBody]
        : actionFailure.kind === 'offline'
          ? [copy.states.offlineTitle, copy.states.offlineBody]
          : actionFailure.kind === 'invalid'
            ? [
                copy.states.saveFailedTitle,
                actionFailure.detail ?? copy.states.saveFailedBody,
              ]
            : actionFailure.kind === 'forbidden'
              ? [
                  copy.states.forbiddenTitle,
                  actionFailure.detail ?? copy.states.failedBody,
                ]
              : [copy.states.saveFailedTitle, copy.states.saveFailedBody];
    return (
      <InlineAlert tone="error" title={title} role="alert">
        {body}
      </InlineAlert>
    );
  }, [actionFailure, copy]);

  return {
    dialog,
    busy,
    actionFailure,
    failureAlert,
    loadDependencyCandidates,
    openDialog,
    closeDialog,
    act,
  };
}
